import React, { useState, useRef, useEffect } from 'react';
import { TextField, Button, Box, Typography, Alert, Link } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { MFAVerification } from './MFAVerification';

interface LoginFormProps {
  onLogin: (token: string) => void;
}

export const LoginForm: React.FC<LoginFormProps> = ({ onLogin }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [requiresMFA, setRequiresMFA] = useState(false);
  const [mfaType, setMfaType] = useState<string>('');
  const [tempToken, setTempToken] = useState<string>('');
  const usernameRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (usernameRef.current) {
      usernameRef.current.focus();
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password })
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Invalid username or password');
      }
      
      const data = await response.json();
      
      if (data.requires_mfa) {
        // MFA required - show verification screen
        setRequiresMFA(true);
        setMfaType(data.mfa_type);
        setTempToken(data.temp_token);
      } else {
        // No MFA - direct login
        onLogin(data.access_token);
      }
    } catch (err: any) {
      setError(err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleMFASuccess = (token: string) => {
    onLogin(token);
  };

  const handleMFACancel = () => {
    setRequiresMFA(false);
    setMfaType('');
    setTempToken('');
    setPassword('');
  };

  if (requiresMFA) {
    return (
      <MFAVerification
        tempToken={tempToken}
        mfaType={mfaType}
        username={username}
        onSuccess={handleMFASuccess}
        onCancel={handleMFACancel}
      />
    );
  }

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ maxWidth: 360, mx: 'auto', mt: 8 }} aria-label="login form">
      <Typography variant="h5" mb={2}>Sign In</Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }} role="alert">{error}</Alert>}
      <TextField
        inputRef={usernameRef}
        label="Username"
        value={username}
        onChange={e => setUsername(e.target.value)}
        fullWidth
        margin="normal"
        required
        autoComplete="username"
        aria-label="username"
        disabled={loading}
      />
      <TextField
        label="Password"
        type="password"
        value={password}
        onChange={e => setPassword(e.target.value)}
        fullWidth
        margin="normal"
        required
        autoComplete="current-password"
        aria-label="password"
        disabled={loading}
      />
      <Button
        type="submit"
        variant="contained"
        color="primary"
        fullWidth
        disabled={loading}
        sx={{ mt: 2 }}
        aria-label="sign in"
      >
        {loading ? 'Signing in...' : 'Sign In'}
      </Button>
      <Box sx={{ mt: 2, textAlign: 'center' }}>
        <Link component={RouterLink} to="/password-reset" underline="hover">
          Forgot password?
        </Link>
        <br />
        <Link component={RouterLink} to="/password-update" underline="hover">
          Set new password
        </Link>
      </Box>
    </Box>
  );
};
